import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Building2, MapPin, Globe, Mail, Phone, User, Pencil, ShieldCheck, ArrowLeft, BadgeCheck, CalendarDays, Receipt, ListTodo, StickyNote, Eye, EyeOff, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ClientModal from "@/components/clients/ClientModal";
import HubSpokeMap from "@/components/clients/HubSpokeMap";
import ClientSubscriptionPanel from "@/components/clients/ClientSubscriptionPanel";
import { endpoints, getTenantScope, setTenantScope } from "@/lib/api";
import { fmtMoney, PAYMENT_STATUS_TONE } from "@/components/billing/billingMeta";
import { StatusBadge } from "@/components/tasks/taskMeta";
import { toast } from "sonner";

const STATUS_TONE = {
  "Certified": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  "Pending Renewal": "bg-blue-50 text-blue-700 border-blue-200",
  "Lapsed": "bg-rose-50 text-rose-700 border-rose-200",
  "Not Certified": "bg-slate-100 text-slate-600 border-slate-200",
};

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function ClientPortalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("hub");
  const [editOpen, setEditOpen] = useState(false);
  const [scope, setScope] = useState(getTenantScope());
  const [me, setMe] = useState(null);

  const load = () => {
    setError(null);
    endpoints.clientPortal(slug)
      .then(setData)
      .catch((e) => {
        setError(e.response?.status === 403 ? "You don't have access to this client's portal." : "Could not load client.");
      });
  };

  useEffect(() => {
    endpoints.me().then(setMe).catch(() => {});
    load();
    const onChange = () => { setScope(getTenantScope()); load(); };
    window.addEventListener("isoquest:scope-changed", onChange);
    return () => window.removeEventListener("isoquest:scope-changed", onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const isSimulating = scope?.role === "client";
  const canEdit = !isSimulating; // Only staff/admin can edit

  const switchToClientView = () => {
    setTenantScope({ role: "client", slug });
    toast.success(`Viewing as ${data?.client?.company_name} client user`);
  };
  const backToStaff = () => {
    setTenantScope(null);
    toast.success("Back to staff view");
  };

  const client = data?.client;
  const upcomingAudits = data?.upcoming_audits || [];
  const invoices = data?.invoices || [];
  const tasks = data?.tasks || [];
  const appts = data?.appointments || [];
  const totals = data?.billing_totals || { billed: 0, paid: 0, outstanding: 0 };

  const primaryContact = useMemo(() => client?.main_contacts?.find((c) => c.is_primary) || client?.main_contacts?.[0], [client]);

  if (error) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4">
            <Lock className="h-5 w-5" />
          </div>
          <h1 data-testid="client-portal-forbidden" className="font-display text-xl font-extrabold mb-1">Access denied</h1>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate("/clients")}><ArrowLeft className="h-4 w-4 mr-1" /> Back to directory</Button>
          {isSimulating && <Button className="ml-2 brand-gradient text-white" onClick={backToStaff}>Back to staff view</Button>}
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {isSimulating && (
        <div className="mb-4 rounded-2xl border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/15 px-4 py-2.5 flex items-center gap-3 text-amber-900 dark:text-amber-100">
          <EyeOff className="h-4 w-4" />
          <div className="text-xs font-semibold">Viewing as <span className="font-bold">{client.company_name}</span> client user — tenant boundary enforced by API.</div>
        </div>
      )}

      <div className="glass-card rounded-3xl p-5 sm:p-6 mb-5">
        <div className="flex items-start gap-4 flex-wrap">
          {!isSimulating && (
            <button onClick={() => navigate("/clients")} className="text-slate-500 hover:text-indigo-600 flex items-center gap-1 text-xs font-semibold mr-2 mt-2">
              <ArrowLeft className="h-3.5 w-3.5" /> Directory
            </button>
          )}
          {client.logo_url ? (
            <img src={client.logo_url} alt={client.company_name} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-indigo-500/25" />
          ) : (
            <div className="h-16 w-16 rounded-2xl brand-gradient text-white text-xl font-bold flex items-center justify-center shadow-md shadow-indigo-500/25">
              {initials(client.company_name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 data-testid="client-portal-name" className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">{client.company_name}</h1>
              <Badge variant="outline" className={`rounded-full ${STATUS_TONE[client.certification_status] || ""}`}>
                <BadgeCheck className="h-3 w-3 mr-1" /> {client.certification_status}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
              {client.industry && <span>{client.industry}</span>}
              {client.address?.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {client.address.city}{client.address.country ? `, ${client.address.country}` : ""}</span>}
              {client.website && <a href={client.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline"><Globe className="h-3 w-3" /> Website</a>}
              {(() => {
                const list = (client.assigned_consultants && client.assigned_consultants.length) ? client.assigned_consultants : (client.assigned_consultant ? [client.assigned_consultant] : []);
                if (!list.length) return null;
                return <span className="flex items-center gap-1" title={list.join(", ")}><User className="h-3 w-3" /> {list.join(", ")}</span>;
              })()}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            {canEdit && me && (
              <>
                <Button data-testid="client-view-as-button" variant="outline" size="sm" onClick={switchToClientView}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> View as client
                </Button>
                <Button data-testid="client-edit-button" className="brand-gradient text-white" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div data-testid="portal-stat-audits" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Upcoming audits</div>
          <div className="text-2xl font-extrabold font-display mt-1 text-indigo-600">{upcomingAudits.length}</div>
        </div>
        <div data-testid="portal-stat-outstanding" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Outstanding</div>
          <div className={`text-2xl font-extrabold font-display font-mono mt-1 ${totals.outstanding > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtMoney(totals.outstanding)}</div>
        </div>
        <div data-testid="portal-stat-open-tasks" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Open tasks</div>
          <div className="text-2xl font-extrabold font-display mt-1 text-amber-600">{tasks.filter((t) => t.status !== "Completed").length}</div>
        </div>
        <div data-testid="portal-stat-certifications" className="rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Certifications</div>
          <div className="text-2xl font-extrabold font-display mt-1 text-purple-600">{(client.iso_standards || []).length}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="portal-tabs" className="h-10 bg-slate-100 dark:bg-slate-800 mb-4 flex-wrap h-auto">
          <TabsTrigger value="hub" data-testid="portal-tab-hub" className="text-xs h-8">ISO Hub</TabsTrigger>
          <TabsTrigger value="overview" data-testid="portal-tab-overview" className="text-xs h-8">Overview</TabsTrigger>
          <TabsTrigger value="certifications" data-testid="portal-tab-certifications" className="text-xs h-8">Certifications</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="portal-tab-calendar" className="text-xs h-8">Calendar</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="portal-tab-tasks" className="text-xs h-8">Tasks</TabsTrigger>
          <TabsTrigger value="billing" data-testid="portal-tab-billing" className="text-xs h-8">Billing</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="portal-tab-subscription" className="text-xs h-8">Subscription</TabsTrigger>
          <TabsTrigger value="notes" data-testid="portal-tab-notes" className="text-xs h-8">Notes</TabsTrigger>
        </TabsList>

        {tab === "hub" && (
          <div className="glass-card rounded-3xl p-4 sm:p-6">
            <HubSpokeMap client={client} onNavigate={(t) => setTab(t)} onClientChanged={() => load()} />
          </div>
        )}

        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="glass-card rounded-3xl p-5 lg:col-span-2 space-y-4">
              <h3 className="font-display font-bold text-[15px]">Contact information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-slate-400" /><div><div className="text-[10px] uppercase tracking-wider text-slate-400">Email</div>{client.primary_email || "—"}</div></div>
                <div className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-slate-400" /><div><div className="text-[10px] uppercase tracking-wider text-slate-400">Phone</div>{client.primary_phone || "—"}</div></div>
                <div className="flex items-start gap-2 sm:col-span-2"><MapPin className="h-4 w-4 mt-0.5 text-slate-400" /><div><div className="text-[10px] uppercase tracking-wider text-slate-400">Address</div>{[client.address?.street, client.address?.city, client.address?.state, client.address?.country, client.address?.postal_code].filter(Boolean).join(", ") || "—"}</div></div>
              </div>

              <h3 className="font-display font-bold text-[15px] pt-2">Main contacts</h3>
              {(client.main_contacts || []).length === 0 ? <p className="text-xs text-slate-500">No contacts recorded.</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {client.main_contacts.map((c, i) => (
                    <div key={i} data-testid={`portal-contact-${i}`} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3 flex items-start gap-3">
                      <Avatar className="h-9 w-9"><AvatarFallback className="brand-gradient text-white text-xs font-semibold">{initials(c.name)}</AvatarFallback></Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold truncate">{c.name}</span>
                          {c.is_primary && <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[9px] rounded-full">Primary</Badge>}
                        </div>
                        <div className="text-[11px] text-slate-500">{c.role || "—"}</div>
                        {c.email && <div className="text-[11px] mt-0.5 truncate">{c.email}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="glass-card rounded-3xl p-5 space-y-3">
              <h3 className="font-display font-bold text-[15px]">Billing summary</h3>
              <div className="rounded-2xl brand-gradient p-4 text-white">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-white/80">Outstanding</div>
                <div className="text-2xl font-extrabold font-display font-mono mt-1">{fmtMoney(totals.outstanding)}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Billed</div>
                  <div className="text-base font-bold font-mono">{fmtMoney(totals.billed)}</div>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Paid</div>
                  <div className="text-base font-bold font-mono text-emerald-600">{fmtMoney(totals.paid)}</div>
                </div>
              </div>
              <div className="pt-2 text-[11px] text-slate-500 space-y-1">
                <div><span className="text-slate-400">Billing email:</span> {client.billing_info?.billing_email || "—"}</div>
                <div><span className="text-slate-400">Terms:</span> {client.billing_info?.payment_terms || "—"}</div>
                <div><span className="text-slate-400">Currency:</span> {client.billing_info?.currency || "ZAR"}</div>
              </div>
            </div>
          </div>
        )}

        {tab === "certifications" && (
          <div className="space-y-3">
            {(client.iso_standards || []).length === 0 ? (
              <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No certifications recorded.</div>
            ) : (
              client.iso_standards.map((s, i) => (
                <div key={i} data-testid={`portal-cert-${i}`} className="glass-card rounded-2xl p-4 flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 flex items-center justify-center shrink-0">
                    <BadgeCheck className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{s.standard}</span>
                      <Badge variant="outline" className={`rounded-full ${STATUS_TONE[s.status] || ""}`}>{s.status}</Badge>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Body: {s.certification_body || "—"} · Certified {fmtDate(s.certification_date)} · Expires {fmtDate(s.expiry_date)}
                    </div>
                    {s.scope && <div className="text-xs mt-1 text-slate-600 dark:text-slate-300">Scope: {s.scope}</div>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "calendar" && (
          <div className="space-y-3">
            {upcomingAudits.length > 0 && (
              <div className="glass-card rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <h3 className="font-display font-bold text-[15px]">Upcoming audits</h3>
                </div>
                <div className="space-y-2">
                  {upcomingAudits.map((a) => (
                    <div key={a.id} data-testid={`portal-audit-${a.id}`} className="rounded-xl border border-amber-200 dark:border-amber-500/25 bg-amber-50/60 dark:bg-amber-500/10 p-3 flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-amber-600" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{a.title}</div>
                        <div className="text-[11px] text-slate-500">{a.date} · {a.time}{a.end_time ? ` – ${a.end_time}` : ""} · {a.type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="glass-card rounded-2xl p-4">
              <h3 className="font-display font-bold text-[15px] mb-3">All appointments</h3>
              {appts.length === 0 ? <p className="text-sm text-slate-500">No appointments yet.</p> : (
                <div className="space-y-2">
                  {appts.map((a) => (
                    <div key={a.id} data-testid={`portal-appt-${a.id}`} className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3 flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-indigo-500" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{a.title}</div>
                        <div className="text-[11px] text-slate-500">{a.date} · {a.time} · {a.type} · {a.consultant || "Unassigned"}</div>
                      </div>
                      <Badge variant="outline" className="text-[10px] rounded-full">{a.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "tasks" && (
          <div className="glass-card rounded-3xl p-4">
            {tasks.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No tasks for this client.</p> : (
              <ul className="space-y-2">
                {tasks.map((t) => (
                  <li key={t.id} data-testid={`portal-task-${t.id}`} className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3 flex items-center gap-3">
                    <ListTodo className="h-4 w-4 text-indigo-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{t.title}</div>
                      <div className="text-[11px] text-slate-500">{t.assignee || "—"} · Due {t.due_date ? fmtDate(t.due_date) : "—"}</div>
                    </div>
                    <StatusBadge status={t.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "billing" && (
          <div className="glass-card rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">Invoice #</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3">Paid</th>
                  <th className="text-right px-4 py-3">Outstanding</th>
                  <th className="text-left px-4 py-3">Due</th>
                  <th className="text-center px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`portal-invoice-${inv.id}`} className="border-t border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-[240px] truncate">{inv.description}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtMoney(inv.amount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmtMoney(inv.amount_paid)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{fmtMoney(inv.outstanding)}</td>
                    <td className="px-4 py-3 text-xs">{inv.due_date}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant="outline" className={`text-[10px] rounded-full ${PAYMENT_STATUS_TONE[inv.status] || ""}`}>{inv.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invoices.length === 0 && <div className="text-center py-8 text-sm text-slate-500">No invoices for this client yet.</div>}
          </div>
        )}

        {tab === "subscription" && (
          <ClientSubscriptionPanel clientSlug={client.slug} clientName={client.company_name} />
        )}

        {tab === "notes" && (
          <div className="glass-card rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="h-4 w-4 text-slate-400" />
              <h3 className="font-display font-bold text-[15px]">Internal notes</h3>
              <Badge variant="outline" className="text-[10px] rounded-full bg-indigo-50 text-indigo-700 border-indigo-200 gap-1 ml-2">
                <ShieldCheck className="h-2.5 w-2.5" /> Staff only
              </Badge>
            </div>
            {client.notes ? (
              <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
            ) : (
              <p className="text-sm text-slate-500">No notes yet. Add some when editing the client.</p>
            )}
          </div>
        )}
      </Tabs>

      <ClientModal
        open={editOpen}
        initial={client}
        onOpenChange={setEditOpen}
        onChanged={() => { load(); setEditOpen(false); }}
      />
    </div>
  );
}
